<script setup lang="ts">
import {Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Input} from "@/core/components/ui/input";
import {inject, ref, watch} from "vue";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import {Label} from "@/core/components/ui/label";
import {Switch} from "@/core/components/ui/switch";
import Rank from "@/core/interfaces/Rank";
import LegacyAccess from "@/core/interfaces/Access";
import {teamApi, teamPlanningApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {CONTRACT_TYPES, jobRoleForRank, parseWeeklyHours} from "@/team/planning";
import {PlanningProfileUpdateRequest} from "@/core/http/resources/TeamPlanningResource";
import {Plus} from "@lucide/vue";

const {ranks, team} = defineProps<{
    ranks: Rank[],
    operatorCodeOptions: { label: string; value: string }[],
    team?: LegacyAccess[],
    /** Renders an icon-only trigger, for the member list header. */
    compact?: boolean
}>();
const emits = defineEmits(['onMemberAdded', 'schedule-updated', 'on-remove-operator-code']);
const {showSuccess, showError} = useToastStore();
const organizationId = useOrganizationId();
const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>,
    cancel?: () => never | Promise<void>
) => Promise<void>;

const rankOptions = ref<{ label: string; value: string }[]>(ranks.map(item => {
    return {label: item.title, value: item.id.toString()};
}));
const contractOptions = [{label: 'Aucun', value: 'null'}, ...CONTRACT_TYPES.map(type => ({label: type.label, value: type.value}))];
const firstName = ref<string>();
const lastName = ref<string>();
const rankId = ref<number>();
const operatorCode = ref<string>();
const contractType = ref<string>('null');
const weeklyHours = ref<string>('');
const isInterim = ref<boolean>(false);
const fieldMissing = ref<{ firstName: boolean; lastName: boolean }>({firstName: false, lastName: false});
const hoursError = ref<boolean>(false);
const modalOpen = ref<boolean>(false);
const loading = ref<boolean>(false);

/**
 * Guard the creation behind the operator-code conflict confirmation, like
 * the fiche edition does.
 */
const create = () => {
    fieldMissing.value = {firstName: !firstName.value?.length, lastName: !lastName.value?.length};

    if (fieldMissing.value.firstName || fieldMissing.value.lastName) {
        return;
    }

    if (parseWeeklyHours(weeklyHours.value) === undefined) {
        hoursError.value = true;

        return;
    }

    hoursError.value = false;

    const code = operatorCode.value;
    const conflictingMember = code && code !== 'null'
        ? team?.find(member => member.operatorCode === code)
        : undefined;

    if (conflictingMember) {
        openConfirmationModal(
            "Ce code opérateur est déjà affecté à un autre membre de votre équipe",
            "Voulez vous vraiment affecter le code à cette personne ?",
            async () => {
                emits('on-remove-operator-code', code);
                await createAccess();
            },
            () => {
                operatorCode.value = undefined;

                return Promise.resolve();
            }
        );

        return;
    }

    return createAccess();
};

const resetForm = () => {
    firstName.value = undefined;
    lastName.value = undefined;
    rankId.value = undefined;
    operatorCode.value = undefined;
    contractType.value = 'null';
    weeklyHours.value = '';
    isInterim.value = false;
    fieldMissing.value = {firstName: false, lastName: false};
    hoursError.value = false;
};

/**
 * Every opening starts from a blank form, whether the previous one ended
 * with a creation or was dismissed. Resetting on open rather than on close
 * keeps the fields from emptying during the closing animation.
 */
watch(modalOpen, open => {
    if (open) {
        resetForm();
    }
});

/**
 * The modal stays as it is while a creation is in flight: the request still
 * reads the form after its first step, so a reopening (which resets it) would
 * seed the new member's fiche with blank values, and the closing at the end
 * of the request would shut the form the user had started again.
 */
const handleOpen = (open: boolean) => {
    if (!loading.value) {
        modalOpen.value = open;
    }
};

/**
 * Create the user access point, then seed its planning profile with the
 * same fields the fiche edition offers (the role deriving from the rank).
 * The two steps report separately: once the access exists the list must
 * refresh even if the seeding fails, or the user recreates a duplicate.
 */
const createAccess = async () => {
    if (!firstName.value || !lastName.value) {
        return;
    }

    if (!organizationId.value) {
        showError({content: 'L\'environnement n\'est pas encore chargé, réessayez dans un instant'});

        return;
    }

    loading.value = true;

    let created;

    try {
        created = await teamApi().createAccess({
            first_name: firstName.value,
            last_name: lastName.value,
            operatorCode: operatorCode.value,
            rankId: rankId.value
        });
    } catch {
        // The member does not exist: keep the modal and its values for a retry.
        showError({content: 'Une erreur est survenue lors de la création du nouveau membre'});
        loading.value = false;

        return;
    }

    emits("onMemberAdded");

    try {
        const accessId = created?.data?.id;

        if (accessId) {
            const schedule = await teamPlanningApi().updateProfile(organizationId.value, accessId, {
                job_role: jobRoleForRank(rankId.value),
                contract_type: contractType.value === 'null' ? null : contractType.value as PlanningProfileUpdateRequest['contract_type'],
                weekly_hours: parseWeeklyHours(weeklyHours.value) ?? null,
                is_interim: isInterim.value
            });

            emits('schedule-updated', schedule);
        }

        showSuccess({content: 'Le nouveau membre a été ajouté avec succès'});
    } catch {
        showError({content: 'Le membre a été créé, mais sa fiche n\'a pas pu être initialisée — complétez-la depuis sa fiche'});
    } finally {
        loading.value = false;
        modalOpen.value = false;
    }
};
</script>

<template>
  <Dialog :open="modalOpen" @update:open="handleOpen">
    <DialogTrigger>
      <button
        v-if="compact"
        title="Ajouter un collaborateur"
        class="flex size-7 items-center justify-center rounded-lg border border-primary bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
      >
        <Plus class="size-4" />
      </button>
      <BaseButton v-else primary>
        Ajouter un membre
      </BaseButton>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          Ajouter un nouveau membre
        </DialogTitle>
      </DialogHeader>
      <div class="flex flex-col gap-5">
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col">
            <Label>Prénom <span class="text-red">*</span></Label>
            <Input v-model="firstName" />
          </div>
          <div class="flex flex-col">
            <Label>Nom <span class="text-red">*</span></Label>
            <Input v-model="lastName" />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col">
            <Label>Poste</Label>
            <BaseSelect
              v-model:model-value="rankId"
              trigger-class="items-start w-full"
              :options="rankOptions"
            />
          </div>
          <div class="flex flex-col">
            <Label>Code opérateur</Label>
            <BaseSelect
              v-model:model-value="operatorCode"
              trigger-class="items-start w-full"
              :options="operatorCodeOptions"
            />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col">
            <Label>Contrat</Label>
            <BaseSelect
              v-model:model-value="contractType"
              trigger-class="items-start w-full"
              :options="contractOptions"
            />
          </div>
          <div class="flex flex-col">
            <Label>Volume hebdo contractuel (h)</Label>
            <Input
              v-model="weeklyHours"
              placeholder="35"
              :class="hoursError ? 'border-red-400 focus-visible:ring-red-400' : ''"
            />
            <p v-if="hoursError" class="text-xs text-red-500">
              Nombre d'heures invalide (entre 0 et 80).
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Switch v-model="isInterim" />
          <Label>Intérimaire</Label>
        </div>
      </div>
      <div class="flex justify-end items-center">
        <p v-if="fieldMissing.lastName || fieldMissing.firstName" class="text-red text-sm pr-4">
          Les champs prénom et nom sont obligatoires.
        </p>
        <BaseButton primary :loading="loading" @click="() => create()">
          Enregistrer
        </BaseButton>
      </div>
    </DialogContent>
  </Dialog>
</template>
